/**
 * CRM UI/API access — зеркало backend/app/crm_permissions.py.
 * Существующие вкладки (заказы, каталог, производство) не затрагиваются.
 */
import { hasFullAdminPowers, isPlatformOwner } from './adminAccess';

export type CrmScope = 'all' | 'assigned' | 'production_read' | 'none';

export type CrmAccessParams = {
  role: string | null;
  telegramId: number | null;
  serverAdminPanelHint?: boolean;
  serverFullAdminHint?: boolean;
  authPlatformOwnerHint?: boolean;
  authCrmScope?: CrmScope | string | null;
  authCrmAccess?: boolean;
};

export function hasPlatformControl(p: CrmAccessParams): boolean {
  if (isPlatformOwner(p.telegramId)) return true;
  if (p.authPlatformOwnerHint === true) return true;
  return false;
}

export function resolveCrmScope(p: CrmAccessParams): CrmScope {
  // Владелец / full admin — не доверяем устаревшему JWT с crm_scope=none в persist store
  if (hasPlatformControl(p) || hasFullAdminPowers(p)) return 'all';
  if (
    p.authCrmScope === 'all' ||
    p.authCrmScope === 'assigned' ||
    p.authCrmScope === 'production_read'
  ) {
    return p.authCrmScope;
  }
  if (p.authCrmAccess === true && p.role === 'manager') return 'assigned';
  if (p.role === 'manager') return 'assigned';
  if (p.authCrmAccess === true) return 'all';
  return 'none';
}

/** Воронка, задачи, чат — не для production sandbox. */
export function canAccessCrmUi(p: CrmAccessParams): boolean {
  if (hasPlatformControl(p) || hasFullAdminPowers(p)) return true;
  if (p.authCrmAccess === true) return true;
  return resolveCrmScope(p) !== 'none';
}

export function canCrmFullControl(p: CrmAccessParams): boolean {
  return resolveCrmScope(p) === 'all';
}

export function canCrmEditClientTags(p: CrmAccessParams): boolean {
  return canCrmFullControl(p);
}

export function canCrmEditDepartment(p: CrmAccessParams): boolean {
  return hasPlatformControl(p);
}

export function canCrmManageLeads(p: CrmAccessParams): boolean {
  const s = resolveCrmScope(p);
  return s === 'all' || s === 'assigned';
}

export function isProductionCrmRead(p: CrmAccessParams): boolean {
  return resolveCrmScope(p) === 'production_read';
}

/** Поля из ответа `/api/auth/*` для store. */
export function authCrmHintsFromApi(data: {
  admin_panel?: boolean;
  full_admin?: boolean;
  platform_owner?: boolean;
  crm_scope?: string;
  crm_access?: boolean;
}) {
  const scope = data.crm_scope;
  const crmScope: CrmScope =
    scope === 'all' || scope === 'assigned' || scope === 'production_read' ? scope : 'none';
  return {
    authAdminHint: Boolean(data.admin_panel),
    authFullAdminHint: Boolean(data.full_admin),
    authPlatformOwnerHint: Boolean(data.platform_owner),
    authCrmScope: crmScope,
    authCrmAccess: Boolean(data.crm_access),
  };
}
